import { Col, message, PageHeader, Row, Select, Space, Spin, Table, Tabs, Tooltip } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import "moment/locale/es";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  LineChartOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  ButtonItem,
  ErrorItem,
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  SwitchItem,
  SelectCuentaContableItem,
  cuentaMask,
  dineroDisabledMask,
} from "../../../../Components/Items";
import ModalMovimientosCuenta from "../../../../Components/ModalMovimientosCuenta";
import {
  MAX_PERIODOS_PDF,
  ROJO_NEGATIVO,
  describirFiltros,
  descargarExcel,
  descargarPdf,
  esNegativo,
} from "./exportarEstadoDeResultadosPorPeriodo";
import GraficaEstadoDeResultados, {
  MAX_SERIES,
  asignarSlotsDeColor,
  rasterizarGrafica,
  seriesDeGrafica,
} from "./GraficaEstadoDeResultados";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

/*
  Estado de Resultados por Periodo: matriz de cuentas (renglones) contra periodos
  (columnas).

  El backend manda `periodos` (el eje) y `renglones` YA en orden de presentacion,
  con las cuentas, los subtotales de cada bloque y las utilidades intercaladas.
  Aqui no se reordena ni se recalcula nada: la tabla, el Excel y el PDF pintan la
  misma estructura para que no puedan decir cosas distintas.

  La tabla usa `Table` de antd y no `TableItem` a proposito: TableItem fija por
  dentro `scroll`, `pagination` y `tableLayout`, e ignora esos props. Aqui hacen
  falta las dos primeras columnas fijas y scroll horizontal.
*/

const COLOR_SUBTOTAL = "#EAF6F8";
const COLOR_UTILIDAD = "#C9EEF3";

const estiloDeRenglon = (renglon) => {
  if (renglon.tipo === "utilidad") {
    return { backgroundColor: COLOR_UTILIDAD, fontWeight: 600 };
  }
  if (renglon.tipo === "subtotal") {
    return { backgroundColor: COLOR_SUBTOTAL, fontWeight: 600 };
  }
  return {};
};

const NOMBRES_BLOQUE = {
  costos: "Costo de ventas",
  gastos: "Gastos de operacion",
  bruta: "Utilidad bruta",
  operacion: "Utilidad de operacion",
  ingresos: "Ingresos",
  productosFinancieros: "Productos financieros",
  gastosFinancieros: "Gastos financieros",
  otrosIngresos: "Otros ingresos",
  otrosGastos: "Otros gastos",
};

const porcentajeTexto = (valor) =>
  valor === null || valor === undefined ? "-" : `${valor}%`;

// Numeros en rojo cuando son negativos, en la tabla igual que en el Excel y el
// PDF (ahi lo hace `colorTexto`). El signo menos sigue presente: el color
// refuerza, no sustituye.
const enRojoSiNegativo = (valor, contenido) =>
  esNegativo(valor) ? <span style={{ color: ROJO_NEGATIVO }}>{contenido}</span> : contenido;

// Preferencia por navegador y por empresa, como `graficoCampo_*` en ReporteVentas.
const claveColapsada = (empresaId) => `graficaColapsadaEstadoResultadosPeriodo_${empresaId}`;

class ReporteEstadoDeResultadosPorPeriodo extends Component {
  formRef = React.createRef();

  // El SVG que nivo dibuja aqui es el que se rasteriza para el PDF y el Excel.
  graficaRef = React.createRef();

  state = {
    loading: false,
    graficaColapsada: localStorage.getItem(claveColapsada(this.props.empresaId)) === "true",
    seleccion: [],
    // id de renglon -> slot de color, para que quitar una serie no repinte las demas
    slotsColor: {},
    dataReporte: null,
    periodos: [],
    renglones: [],
    parametros: null,
    objetoProyectos: null,
    objetoUnidadesDeNegocio: null,
    ultimosFiltros: null,
    modalMovimientos: false,
    cuentaMovimientos: null,
    periodoMovimientos: null,
    subtituloMovimientos: null,
  };

  atras() {
    this.props.history.goBack();
  }

  async componentDidMount() {
    this.setState({ loading: true });
    await axios
      .post(
        "proyectos/selectProyectos",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          this.setState({
            objetoProyectos: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.nombre}
              </Option>
            )),
          });
        }
      });
    await axios
      .post(
        "unidadesdenegocio/selectUnidadesDeNegocio",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          this.setState({
            objetoUnidadesDeNegocio: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {cuentaMask(
                  (value.nivel1 || "") +
                    (value.nivel2 || "") +
                    (value.nivel3 || "") +
                    (value.nivel4 || "") +
                    (value.nivel5 || "")
                ) +
                  " - " +
                  value.nombre}
              </Option>
            )),
          });
        }
      });

    this.consultarReporte({
      periodo: [moment().startOf("year"), moment()],
      periodicidad: "mensual",
      nivel: 3,
      filtrarProyecto: 0,
      comparativo: false,
      porcentajes: false,
    });
  }

  consultarReporte(values) {
    this.setState({ loading: true });
    axios
      .post(
        "reportesFinancieros/reporteEstadoDeResultadosPorPeriodo",
        { ...values, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            dataReporte: res.data,
            periodos: res.data.periodos,
            renglones: res.data.renglones,
            parametros: res.data.parametros,
            // Los mismos filtros con los que se consulto: el drill-down tiene que
            // mirar exactamente el mismo universo de movimientos que la celda.
            ultimosFiltros: {
              filtrarProyecto: values.filtrarProyecto,
              proyectos: values.proyectos,
              unidadDeNegocioId: values.unidadDeNegocioId,
              cuentasExcluidas: values.cuentasExcluidas || [],
            },
          });
        } else if (res.data.success === 1) {
          message.warning(res.data.mensaje || "No se pudo generar el reporte");
          this.setState({ loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargo bien el reporte");
        this.setState({ loading: false });
      });
  }

  nombreProyectos() {
    const data = this.state.dataReporte;
    if (!data) return "";
    if (data.filtrarProyecto === 1 && data.proyectos && data.proyectos.length > 0) {
      return `Proyectos: ${data.proyectos.map((p) => p.nombre).join(", ")}`;
    }
    if (data.filtrarProyecto === 2 && data.unidadDeNegocio && data.unidadDeNegocio.nombre) {
      return `Unidad de negocio: ${data.unidadDeNegocio.nombre}`;
    }
    return "";
  }

  descripcionFiltros() {
    return describirFiltros({
      parametros: this.state.parametros,
      nombreProyectos: this.nombreProyectos(),
    });
  }

  cambiarColapsada = () => {
    const colapsada = !this.state.graficaColapsada;
    localStorage.setItem(claveColapsada(this.props.empresaId), String(colapsada));
    this.setState({ graficaColapsada: colapsada });
  };

  cambiarSeleccion = (claves) => {
    this.setState((estado) => ({
      seleccion: claves,
      slotsColor: asignarSlotsDeColor(claves, estado.slotsColor),
    }));
  };

  series() {
    return seriesDeGrafica(this.state.dataReporte, this.state.seleccion, this.state.slotsColor);
  }

  async exportar(descargar) {
    const { dataReporte } = this.state;
    if (!dataReporte) return;
    // La grafica se rasteriza en el momento, desde el mismo SVG que esta en
    // pantalla: asi el archivo no puede mostrar una grafica distinta a la vista.
    const grafica = await rasterizarGrafica(this.graficaRef.current);
    const resultado = await descargar({
      datos: dataReporte,
      empresaNombre: dataReporte.empresa && dataReporte.empresa.nombre,
      descripcionFiltros: this.descripcionFiltros(),
      grafica,
    });
    // `descargarPdf` no devuelve nada; solo el Excel reporta si logro incrustarla.
    const sinGrafica = !grafica || (resultado && resultado.conGrafica === false);
    if (sinGrafica) message.warning("El archivo se descargo sin la grafica");
  }

  abrirMovimientos(renglon, periodo, anioAnterior) {
    if (!renglon.drill) return;
    // Los "efectivos" son los recortados al rango consultado: son los que suman
    // exactamente lo que dice la celda. En el ano anterior llevan ademas el mismo
    // recorte trasladado, para que un periodo parcial no abra el mes completo.
    const inicio = anioAnterior ? periodo.inicioAnteriorEfectivo : periodo.inicioEfectivo;
    const fin = anioAnterior ? periodo.finAnteriorEfectivo : periodo.finEfectivo;
    if (!inicio || !fin) return;
    this.setState({
      modalMovimientos: true,
      cuentaMovimientos: {
        _id: renglon.cuentaContableId,
        cuenta: renglon.codigo,
        descripcion: renglon.descripcion,
      },
      periodoMovimientos: [inicio, fin],
      subtituloMovimientos: anioAnterior ? periodo.labelAnterior : periodo.label,
    });
  }

  cerrarMovimientos = () => {
    this.setState({ modalMovimientos: false, cuentaMovimientos: null, periodoMovimientos: null });
  };

  celdaImporte(valor, renglon, periodo, anioAnterior) {
    const contenido = dineroDisabledMask(valor || 0);
    const color = esNegativo(valor) ? ROJO_NEGATIVO : undefined;
    if (!renglon.drill) return enRojoSiNegativo(valor, contenido);
    return (
      <span
        style={{ color, cursor: "pointer", textDecoration: "underline dotted" }}
        onClick={() => this.abrirMovimientos(renglon, periodo, anioAnterior)}
      >
        {contenido}
      </span>
    );
  }

  construirColumnas() {
    const { periodos, parametros } = this.state;
    const comparativo = !!(parametros && parametros.comparativo);
    const porcentajes = !!(parametros && parametros.porcentajes);
    const agrupar = comparativo || porcentajes;
    const celdaConEstilo = (renglon) => ({ style: estiloDeRenglon(renglon) });

    const encabezado = (periodo) => (
      <div style={{ textAlign: "center" }}>
        <div>{periodo.label}</div>
        {periodo.subLabel ? (
          <div style={{ fontSize: 10, color: "#8c8c8c", fontWeight: 400 }}>{periodo.subLabel}</div>
        ) : null}
      </div>
    );

    const columnas = [
      {
        title: "Cuenta",
        key: "cuenta",
        dataIndex: "codigo",
        fixed: "left",
        width: 140,
        onCell: celdaConEstilo,
        render: (valor, renglon) => (renglon.tipo === "cuenta" ? cuentaMask(valor || "") : ""),
      },
      {
        title: "Concepto",
        key: "concepto",
        dataIndex: "descripcion",
        fixed: "left",
        width: 300,
        ellipsis: true,
        onCell: celdaConEstilo,
        render: (valor, renglon) => (
          <span style={{ paddingLeft: renglon.tipo === "cuenta" ? (renglon.sangria || 0) * 14 : 0 }}>
            {valor}
          </span>
        ),
      },
    ];

    periodos.forEach((periodo) => {
      const colImporte = {
        title: agrupar ? "Importe" : encabezado(periodo),
        key: `${periodo.key}-importe`,
        dataIndex: ["importes", periodo.key],
        align: "right",
        width: 130,
        onCell: celdaConEstilo,
        render: (valor, renglon) => this.celdaImporte(valor, renglon, periodo, false),
      };

      if (!agrupar) {
        columnas.push(colImporte);
        return;
      }

      const hijos = [colImporte];
      if (comparativo) {
        hijos.push({
          title: "A.A.",
          key: `${periodo.key}-anterior`,
          dataIndex: ["importesAnterior", periodo.key],
          align: "right",
          width: 130,
          onCell: celdaConEstilo,
          // Sin par en el anio anterior (una semana 53, por ejemplo) la celda
          // queda en "-" en vez de fingir un cero.
          render: (valor, renglon) =>
            periodo.keyAnterior ? this.celdaImporte(valor, renglon, periodo, true) : "-",
        });
        hijos.push({
          title: "Var %",
          key: `${periodo.key}-variacion`,
          dataIndex: ["variacionPct", periodo.key],
          align: "right",
          width: 90,
          onCell: celdaConEstilo,
          render: (valor) => enRojoSiNegativo(valor, porcentajeTexto(valor)),
        });
      }
      if (porcentajes) {
        hijos.push({
          title: "%",
          key: `${periodo.key}-porcentaje`,
          dataIndex: ["porcentaje", periodo.key],
          align: "right",
          width: 80,
          onCell: celdaConEstilo,
          render: (valor) => enRojoSiNegativo(valor, porcentajeTexto(valor)),
        });
      }

      columnas.push({ title: encabezado(periodo), key: periodo.key, children: hijos });
    });

    const colTotal = {
      title: agrupar ? "Importe" : "Total",
      key: "total",
      dataIndex: "total",
      align: "right",
      width: 140,
      fixed: "right",
      onCell: celdaConEstilo,
      render: (valor) => enRojoSiNegativo(valor, dineroDisabledMask(valor || 0)),
    };

    if (!agrupar) {
      columnas.push(colTotal);
      return columnas;
    }

    const hijosTotal = [colTotal];
    if (comparativo) {
      hijosTotal.push({
        title: "A.A.",
        key: "totalAnterior",
        dataIndex: "totalAnterior",
        align: "right",
        width: 140,
        fixed: "right",
        onCell: celdaConEstilo,
        render: (valor) => enRojoSiNegativo(valor, dineroDisabledMask(valor || 0)),
      });
      hijosTotal.push({
        title: "Var %",
        key: "totalVariacion",
        dataIndex: "variacionPctTotal",
        align: "right",
        width: 90,
        fixed: "right",
        onCell: celdaConEstilo,
        render: (valor) => enRojoSiNegativo(valor, porcentajeTexto(valor)),
      });
    }
    if (porcentajes) {
      hijosTotal.push({
        title: "%",
        key: "totalPorcentaje",
        dataIndex: "porcentajeTotal",
        align: "right",
        width: 80,
        fixed: "right",
        onCell: celdaConEstilo,
        render: (valor) => enRojoSiNegativo(valor, porcentajeTexto(valor)),
      });
    }

    columnas.push({ title: "Total", key: "totalGrupo", fixed: "right", children: hijosTotal });
    return columnas;
  }

  bloqueGrafica() {
    const { graficaColapsada, seleccion, periodos } = this.state;
    const series = this.series();
    const excedidas = seleccion.length > MAX_SERIES ? seleccion.length - MAX_SERIES : 0;

    // Colapsada, la grafica NO se desmonta: se manda fuera de pantalla con un
    // tamano fijo. Si se desmontara, no habria SVG que rasterizar y el Excel y
    // el PDF saldrian sin grafica justo cuando el usuario la tiene escondida.
    const estiloLienzo = graficaColapsada
      ? { position: "absolute", left: -99999, top: 0, width: 900, height: 320, pointerEvents: "none" }
      : { height: 320 };

    return (
      <div
        style={{
          backgroundColor: "white",
          padding: 20,
          paddingTop: 10,
          borderRadius: 5,
          marginTop: 10,
          boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <LineChartOutlined style={{ color: "#00BFD6" }} />
            <span style={{ fontWeight: 600 }}>Grafica</span>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>
              {seleccion.length > 0
                ? `${series.length} renglon(es) seleccionado(s)`
                : "Ingresos, costos, gastos y utilidad neta"}
            </span>
            {seleccion.length > 0 && (
              <ButtonItem blanco size="small" onClick={() => this.cambiarSeleccion([])}>
                Quitar seleccion
              </ButtonItem>
            )}
          </div>
          <ButtonItem
            blanco
            size="small"
            icon={graficaColapsada ? <DownOutlined /> : <UpOutlined />}
            onClick={this.cambiarColapsada}
          >
            {graficaColapsada ? "Mostrar" : "Ocultar"}
          </ButtonItem>
        </div>

        {!graficaColapsada && (
          <div style={{ fontSize: 12, color: "#8c8c8c", margin: "4px 0 0" }}>
            Palomea renglones de la tabla para graficarlos en lugar de los titulares.
            {excedidas > 0 &&
              ` Se grafican las primeras ${MAX_SERIES} series; ${excedidas} quedaron fuera.`}
          </div>
        )}

        <div ref={this.graficaRef} style={estiloLienzo}>
          <GraficaEstadoDeResultados series={series} periodos={periodos} />
        </div>
      </div>
    );
  }

  avisoBloquesOmitidos() {
    const omitidos = (this.state.parametros && this.state.parametros.bloquesOmitidos) || [];
    if (omitidos.length === 0) return null;
    return (
      <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 8 }}>
        {`Sin cuentas en el catalogo para: ${omitidos
          .map((clave) => NOMBRES_BLOQUE[clave] || clave)
          .join(", ")}. Esos renglones no se muestran.`}
      </div>
    );
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  render() {
    const { dataReporte, periodos } = this.state;
    const pdfDemasiadoAncho = periodos.length > MAX_PERIODOS_PDF;

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteEstadoDeResultadosPorPeriodo"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Reporte Estado de Resultados por Periodo"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {dataReporte && (
                    <ButtonItem
                      blanco
                      onClick={() => this.exportar(descargarExcel)}
                      icon={<FileExcelOutlined />}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  {dataReporte && (
                    <Tooltip
                      title={
                        pdfDemasiadoAncho
                          ? `El PDF solo cabe con ${MAX_PERIODOS_PDF} periodos o menos. Usa una periodicidad mas amplia o acorta el rango.`
                          : ""
                      }
                    >
                      <span>
                        <ButtonItem
                          blanco
                          disabled={pdfDemasiadoAncho}
                          onClick={() => this.exportar(descargarPdf)}
                          icon={<FilePdfOutlined />}
                        >
                          Descargar PDF
                        </ButtonItem>
                      </span>
                    </Tooltip>
                  )}
                  <ButtonItem type="primary" htmlType="submit">
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={this.state.loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <Row>
                    <Col span={12}>
                      <FormItem
                        initialValue={[moment().startOf("year"), moment()]}
                        name="periodo"
                        required
                        noStyle
                      >
                        <RangePickerItem />
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem
                        label="Periodicidad"
                        name="periodicidad"
                        required
                        marginBottom="0px"
                        initialValue="mensual"
                      >
                        <SelectItem placeholder="Periodicidad">
                          <Option value="mensual">Mensual</Option>
                          <Option value="semanal">Semanal (lunes a domingo)</Option>
                          <Option value="quincenal">Quincenal</Option>
                          <Option value="trimestral">Trimestral</Option>
                          <Option value="anual">Anual</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Proyecto">
                  <Row>
                    <Col span={8}>
                      <FormItem name="filtrarProyecto" required noStyle initialValue={0}>
                        <SelectItem width="90%" placeholder="Filtrar Proyecto">
                          <Option value={0}>Niguno</Option>
                          <Option value={1}>Proyectos</Option>
                          <Option value={2}>Unidades de negocio</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col span={8}>
                      <FormItem shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          const filtrarProyecto = getFieldValue("filtrarProyecto");
                          if (filtrarProyecto === 1) {
                            return (
                              <FormItem required name="proyectos" noStyle>
                                <SelectItem width="90%" placeholder="Proyectos" mode="multiple">
                                  {this.state.objetoProyectos}
                                </SelectItem>
                              </FormItem>
                            );
                          }
                          if (filtrarProyecto === 2) {
                            return (
                              <FormItem required name="unidadDeNegocioId" noStyle>
                                <SelectItem width="90%" placeholder="Unidades de Negocio">
                                  {this.state.objetoUnidadesDeNegocio}
                                </SelectItem>
                              </FormItem>
                            );
                          }
                          return null;
                        }}
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Nivel">
                  <FormItem label="Nivel" name="nivel" required marginBottom="0px" initialValue={3}>
                    <SelectItem placeholder="Nivel">
                      <Option value={1}>Nivel 1</Option>
                      <Option value={2}>Nivel 2</Option>
                      <Option value={3}>Nivel 3</Option>
                      <Option value={4}>Nivel 4</Option>
                      <Option value={5}>Nivel 5</Option>
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Rango de Cuentas">
                  <Row>
                    <Col span={12}>
                      <FormItem name="cuentaIdDesde" noStyle>
                        <SelectCuentaContableItem
                          width="90%"
                          placeholder="Cuenta Desde"
                          allowClear
                          empresaId={this.props.empresaId}
                          headersToken={this.props.headersToken}
                        />
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem name="cuentaIdHasta" noStyle>
                        <SelectCuentaContableItem
                          width="90%"
                          placeholder="Cuenta Hasta"
                          allowClear
                          empresaId={this.props.empresaId}
                          headersToken={this.props.headersToken}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabKey="5" key="5" forceRender tab="Cuentas a Omitir">
                  <FormItem name="cuentasExcluidas" noStyle>
                    <SelectCuentaContableItem
                      width="100%"
                      mode="multiple"
                      allowClear
                      placeholder="Cuentas que no se toman en cuenta"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                    />
                  </FormItem>
                  <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 6 }}>
                    Las cuentas omitidas se descuentan del calculo completo: no aparecen, no suman a
                    sus cuentas padre y no entran en la utilidad. Omitir una cuenta de mayor omite
                    tambien todas sus subcuentas. La seleccion no se guarda.
                  </div>
                </TabPane>
                <TabPane tabKey="6" key="6" forceRender tab="Comparar">
                  <Row>
                    <Col span={12}>
                      <FormItem
                        label="Comparar vs ano anterior"
                        name="comparativo"
                        valuePropName="checked"
                        marginBottom="0px"
                        initialValue={false}
                      >
                        <SwitchItem />
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem
                        label="% sobre ingresos"
                        name="porcentajes"
                        valuePropName="checked"
                        marginBottom="0px"
                        initialValue={false}
                      >
                        <SwitchItem />
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
              </Tabs>
            </div>

            {dataReporte && this.bloqueGrafica()}

            <div style={{ marginTop: 10 }}>
              <Table
                className="custom-small-table"
                columns={this.construirColumnas()}
                dataSource={this.state.renglones}
                rowKey={(renglon) => renglon.id}
                size="small"
                bordered
                pagination={false}
                scroll={{ x: "max-content", y: "60vh" }}
                rowSelection={{
                  selectedRowKeys: this.state.seleccion,
                  onChange: this.cambiarSeleccion,
                  columnWidth: 40,
                  fixed: true,
                }}
              />
              {this.avisoBloquesOmitidos()}
            </div>
          </Spin>
        </Form>
        <ModalMovimientosCuenta
          visible={this.state.modalMovimientos}
          cuenta={this.state.cuentaMovimientos}
          periodo={this.state.periodoMovimientos}
          subtitulo={this.state.subtituloMovimientos}
          filtros={this.state.ultimosFiltros}
          permisoEditarPolizas={this.props.permisoEditarPolizas}
          editarTodasLasPolizas={this.props.editarTodasLasPolizas}
          onCancel={this.cerrarMovimientos}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
    // Los usa ModalMovimientosCuenta para decidir si deja editar la poliza del
    // drill-down. Mismos que en ReporteBalanzaDeComprobacion.
    permisoEditarPolizas:
      state.user.tipo === 0 ||
      state.user.permisos.find(
        (valueTodos) => valueTodos.permisoId.toString() === "615cea8ff30e81b54183cfbf"
      )?.activado,
    editarTodasLasPolizas: state.empresa.editarTodasLasPolizas,
  };
};

export default connect(mapStateToProps)(ReporteEstadoDeResultadosPorPeriodo);
